import {apiGet, apiPost, postForm} from '../../utils/requests';
import {SWFT_DIGITAL_API_HOST} from '../../constants';
import Cookies from 'js-cookie';
import {generateRandomId} from '../../utils/helper';
import {FileData} from '../../reducers/media';

const getFilesEndpoint = SWFT_DIGITAL_API_HOST + '/media/trial';
const generateAnnotationEndpoint = SWFT_DIGITAL_API_HOST + '/media/annotations/';
const uploadFileEndPoint = SWFT_DIGITAL_API_HOST + '/media/anonymous/upload';

export interface QuickTrialData {
    files: FileData[];
    trial_org_uid: string;
    allow_more_uploads: boolean;
    access_token: string;
    api_key?: string;
}

export interface MiniFileData {
    uid: string;
    name: string;
    cdn_url: string;
    caption: string;
    tags: Array<string>;
    type: string;
    file_size: number;
}

export const getSessionId = (): string => {
    const sessionId = Cookies.get('pxl-session-id');
    if (sessionId) {
        return sessionId;
    } else {
        const newSessionId = generateRandomId();
        Cookies.set('pxl-session-id', newSessionId, {expires: 365}); // Set cookie to expire in 1 year
        return newSessionId;
    }
};

export const setTrialAccessToken = (accessToken: string) => {
    Cookies.set('trial_access_token', accessToken);
};

export const getTrialAccessToken = () => {
    return Cookies.get('trial_access_token');
};

export const getQuickTrialSetupData = async () => {
    const response = await apiGet<QuickTrialData>(getFilesEndpoint + '?session_id=' + getSessionId());
    if (response.status === 200 && response.data && response.data['access_token'].length > 0) {
        setTrialAccessToken(response.data['access_token']);
    }
    return response;
};

export const convertToMiniFile = (file: FileData) => {
    return {
        uid: file.uid,
        name: file.name,
        cdn_url: file.cdn_url,
        caption: file.caption?.text ?? '',
        tags: file.tags?.map((tag) => tag.text) ?? [],
        type: file.type,
        file_size: file.file_size,
    };
};

export const generateTags = async (media_uid: string, org_uid: string) => {
    const accessToken = getTrialAccessToken();
    if (accessToken === undefined || accessToken.length < 1) {
        return {
            status: 400,
            error: 'Something went wrong while generating tags',
            data: undefined,
        };
    }
    return await apiPost<any>(
        generateAnnotationEndpoint,
        {
            org_uid: org_uid,
            media_uid: media_uid,
            action: 'generate_tags',
        },
        {
            Authorization: 'Bearer ' + accessToken,
        },
    );
};

export const generateCaption = async (media_uid: string, org_uid: string) => {
    const accessToken = getTrialAccessToken();
    if (accessToken === undefined || accessToken.length < 1) {
        return {
            status: 400,
            error: 'Something went wrong while generating caption',
            data: undefined,
        };
    }
    return await apiPost<any>(
        generateAnnotationEndpoint,
        {
            org_uid: org_uid,
            media_uid: media_uid,
            action: 'generate_caption',
        },
        {
            Authorization: 'Bearer ' + accessToken,
        },
    );
};

export const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('size', file.size.toString());
    formData.append('type', file.type);
    formData.append('session_id', getSessionId());
    try {
        const response = await postForm(uploadFileEndPoint, formData, {});
        const file = response['file'];
        const miniFile = convertToMiniFile(file);
        return {
            status: 'success',
            file: miniFile,
        };
    } catch (e: any) {
        return {
            status: 'failed',
            file: null,
            msg: e.message,
        };
    }
};
